import { Grid } from 'antd';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type ModelConfiguratorDetailsFragment } from '../../../../api/fragments/ModelConfiguratorDetails';
import { BannerTextPosition } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import Image from '../../../../components/Image';
import { useThemeComponents } from '../../../../themes';
import breakpoints from '../../../../utilities/breakpoints';
import { allowedExtensions } from '../../../../utilities/extensions';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import useCountryOptions from '../../../admin/CompanyDetailsPage/useCountryOptions';
import AffixWrapper from './components/AffixWrapper';

const Video = styled.video`
    max-width: 100vw;
    object-fit: cover;

    @media (min-width: ${breakpoints.md}) {
        aspect-ratio: 16/9;
    }

    @media (max-width: ${breakpoints.sm}) {
        aspect-ratio: 1/1;
    }
`;

export const StyledImage = styled(Image)`
    min-width: 100vw;
    height: auto;
    max-height: 55vh;
    object-fit: cover;
`;

export const BannerContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    position: relative;
`;

export const BannerText = styled.div<{ $textAlign: BannerTextPosition }>`
    width: 100%;
    height: 100%;
    position: absolute;
    color: #fff;
    display: flex;
    align-items: center;
    white-space: pre-wrap;
    font-size: 1.5rem;
    line-height: 1;
    padding: 0 24px;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 0 120px;
    }

    @media screen and (min-width: ${breakpoints.xl}) {
        font-size: 3rem;
    }

    ${({ $textAlign }) => {
        switch ($textAlign) {
            case BannerTextPosition.Centre:
                return 'justify-content: center; text-align: center;';

            case BannerTextPosition.Right:
                return 'justify-content: flex-end; text-align: right;';

            default:
                return 'justify-content: flex-start; text-align: left;';
        }
    }};
`;
export type BannerProps = {
    modelConfigurator: ModelConfiguratorDetailsFragment;
    showExternalLink: boolean;
    setShowExternalLink: (show: boolean) => void;
    setExternalLinkHeight?: (height: number) => void;
};

const Link = ({ href, companyName, country }: { href: string; companyName: string; country: string }) => (
    <a href={href}>{companyName}</a>
);

type ExternalLinkContentProps = Pick<BannerProps, 'setExternalLinkHeight'> & {
    url: string;
    onClose: () => void;
};

export const ExternalLinkContent = ({ url, onClose, setExternalLinkHeight }: ExternalLinkContentProps) => {
    useTranslation('configuratorDetails');

    const { ConfiguratorSubHeader, CloseButton } = useThemeComponents();

    const externalLinkRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (externalLinkRef.current) {
            const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
                if (setExternalLinkHeight) {
                    for (const entry of entries) {
                        if (entry instanceof ResizeObserverEntry) {
                            const contentHeight = entry?.borderBoxSize[0]?.blockSize;
                            if (Number.isFinite(contentHeight)) {
                                setExternalLinkHeight(contentHeight);
                            }
                        }
                    }
                }
            });

            observer.observe(externalLinkRef.current);

            return () => {
                if (externalLinkRef.current) {
                    observer.unobserve(externalLinkRef.current);
                }
            };
        }

        return () => {};
    }, []);

    const translatedString = useTranslatedString();
    const company = useCompany();
    const countryOptions = useCountryOptions();
    const { companyName, country } = useMemo(
        () => ({
            companyName: translatedString(company.companyName),
            country: countryOptions.find(option => option.value === company.countryCode)?.label,
        }),
        [company.countryCode, company.companyName, countryOptions, translatedString]
    );

    const StyledCloseButton = styled(CloseButton)`
        position: absolute;
        top: 50%;
        right: 1.5rem;
        transform: translate(0, -50%);
    `;

    return (
        <ConfiguratorSubHeader ref={externalLinkRef} style={{ position: 'relative' }}>
            <span>
                <Trans
                    components={{
                        link: <Link companyName={companyName} country={country} href={`https://${url}`} />,
                        bold: <strong />,
                    }}
                    i18nKey="configuratorDetails:externalLinkDescription"
                    values={{ companyName }}
                />
            </span>

            <StyledCloseButton onClick={onClose} type="link" />
        </ConfiguratorSubHeader>
    );
};

const Banner = ({ modelConfigurator, showExternalLink, setShowExternalLink, setExternalLinkHeight }: BannerProps) => {
    const screens = Grid.useBreakpoint();
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        videoRef.current?.load();
    }, []);

    const bannerExtension = useMemo(() => {
        if (screens.md && modelConfigurator.banner?.filename) {
            return modelConfigurator.banner?.filename.split('.').pop();
        }

        if (!screens.md && modelConfigurator.mobileBanner?.filename) {
            return modelConfigurator.mobileBanner?.filename.split('.').pop();
        }

        return (
            modelConfigurator.banner?.filename.split('.').pop() ??
            modelConfigurator.mobileBanner?.filename.split('.').pop() ??
            null
        );
    }, [modelConfigurator.banner?.filename, modelConfigurator.mobileBanner?.filename, screens.md]);

    const bannerAsset: string = useMemo(() => {
        if (screens.md && modelConfigurator.banner?.url) {
            return modelConfigurator.banner.url;
        }

        if (!screens.md && modelConfigurator.mobileBanner?.url) {
            return modelConfigurator.mobileBanner.url;
        }

        return modelConfigurator.banner?.url ?? modelConfigurator.mobileBanner?.url ?? null;
    }, [modelConfigurator.banner?.url, modelConfigurator.mobileBanner?.url, screens.md]);

    const onExternalLinkClose = useCallback(() => setShowExternalLink(false), [setShowExternalLink]);

    const videoUrlWithTimestamp = `${bannerAsset}?t=${Date.now()}`;

    const translate = useTranslatedString();

    return (
        <>
            {showExternalLink && (
                <AffixWrapper>
                    <ExternalLinkContent
                        onClose={onExternalLinkClose}
                        setExternalLinkHeight={setExternalLinkHeight}
                        url={modelConfigurator.externalUrl}
                    />
                </AffixWrapper>
            )}
            <BannerContainer>
                {allowedExtensions.video.includes(`.${bannerExtension}`) && (
                    <Video ref={videoRef} src={videoUrlWithTimestamp} autoPlay loop muted playsInline>
                        <source src={videoUrlWithTimestamp} type={`video/${bannerExtension}`} />
                    </Video>
                )}
                {allowedExtensions.image.includes(`.${bannerExtension}`) && (
                    <StyledImage preview={false} src={videoUrlWithTimestamp} />
                )}
                {translate(modelConfigurator.bannerText) && (
                    <BannerText $textAlign={modelConfigurator.bannerTextPosition}>
                        {translate(modelConfigurator.bannerText)}
                    </BannerText>
                )}
            </BannerContainer>
        </>
    );
};

export const useBanner = (modelConfigurator: ModelConfiguratorDetailsFragment) => {
    const [showExternalLink, setShowExternalLink] = useState<boolean>(!!modelConfigurator.externalUrl);
    const [externalLinkHeight, setExternalLinkHeight] = useState<number | null>(null);

    return {
        externalLinkHeight,
        showExternalLink,
        render: () => (
            <Banner
                modelConfigurator={modelConfigurator}
                setExternalLinkHeight={setExternalLinkHeight}
                setShowExternalLink={setShowExternalLink}
                showExternalLink={showExternalLink}
            />
        ),
    };
};
